import type { CSSProperties } from 'react';
import {
  ADD_NOTE_OPTIONS,
  addNoteToVoicing,
  defaultVoicing,
  formatAddOptionLabel,
  freqToSolfegeLabel,
  MAX_CHORD_NOTES,
  removeNoteAt,
  shiftNoteOctave,
  shiftVoicingOctave,
} from '../utils/chordVoicing';
import { previewChordFrequencies } from '../audio/celestialPadEngine';
import { CHORD_COLORS, CHORD_LABELS, type ChordId } from '../audio/celestialPadEngine';

interface Props {
  chordId: ChordId;
  notes: number[];
  isCustom: boolean;
  onChange: (notes: number[]) => void;
  onReset: () => void;
  onClose: () => void;
}

export function ChordVoicingEditor({
  chordId,
  notes,
  isCustom,
  onChange,
  onReset,
  onClose,
}: Props) {
  const preview = () => previewChordFrequencies(notes);

  return (
    <div
      className="chord-voicing-editor"
      style={{ '--chord-color': CHORD_COLORS[chordId] } as CSSProperties}
    >
      <header className="chord-voicing-editor__header">
        <div>
          <h3 className="chord-voicing-editor__title">Notas de {CHORD_LABELS[chordId]}</h3>
          <p className="chord-voicing-editor__subtitle">
            Octava las notas o añade otras de la escala de Do.
          </p>
        </div>
        <button
          type="button"
          className="chord-voicing-editor__close"
          onClick={onClose}
          aria-label="Cerrar editor"
        >
          ×
        </button>
      </header>

      <div className="chord-voicing-editor__global">
        <span className="chord-voicing-editor__label">Todo el acorde</span>
        <button
          type="button"
          className="chord-voicing-editor__oct"
          onClick={() => {
            onChange(shiftVoicingOctave(notes, -1));
            preview();
          }}
          title="Bajar una octava"
        >
          − Oct
        </button>
        <button
          type="button"
          className="chord-voicing-editor__oct"
          onClick={() => {
            onChange(shiftVoicingOctave(notes, 1));
            preview();
          }}
          title="Subir una octava"
        >
          + Oct
        </button>
        <button type="button" className="chord-voicing-editor__preview" onClick={preview}>
          ▶ Escuchar
        </button>
        {isCustom && (
          <button type="button" className="chord-voicing-editor__reset" onClick={onReset}>
            Restaurar
          </button>
        )}
      </div>

      <ul className="chord-voicing-editor__notes">
        {notes.map((freq, index) => (
          <li key={`${freq}-${index}`} className="chord-voicing-editor__note">
            <span className="chord-voicing-editor__note-name">{freqToSolfegeLabel(freq)}</span>
            <div className="chord-voicing-editor__note-actions">
              <button
                type="button"
                className="chord-voicing-editor__mini"
                onClick={() => {
                  const next = [...notes];
                  next[index] = shiftNoteOctave(freq, -1);
                  onChange(next.sort((a, b) => a - b));
                  preview();
                }}
                aria-label="Bajar octava"
              >
                −
              </button>
              <button
                type="button"
                className="chord-voicing-editor__mini"
                onClick={() => {
                  const next = [...notes];
                  next[index] = shiftNoteOctave(freq, 1);
                  onChange(next.sort((a, b) => a - b));
                  preview();
                }}
                aria-label="Subir octava"
              >
                +
              </button>
              <button
                type="button"
                className="chord-voicing-editor__mini chord-voicing-editor__mini--remove"
                disabled={notes.length <= 1}
                onClick={() => onChange(removeNoteAt(notes, index))}
                aria-label="Quitar nota"
              >
                ×
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className="chord-voicing-editor__add">
        <span className="chord-voicing-editor__label">
          Añadir nota {notes.length >= MAX_CHORD_NOTES ? '(máximo)' : ''}
        </span>
        <div className="chord-voicing-editor__palette">
          {ADD_NOTE_OPTIONS.map((opt) => {
            const taken = notes.some((f) => Math.abs(f - opt.freq) < 0.5);
            return (
              <button
                key={`${opt.note}-${opt.octave}`}
                type="button"
                className="chord-voicing-editor__add-btn"
                style={{ '--note-color': opt.color } as CSSProperties}
                disabled={taken || notes.length >= MAX_CHORD_NOTES}
                onClick={() => {
                  onChange(addNoteToVoicing(notes, opt.freq));
                  preview();
                }}
                title={formatAddOptionLabel(opt.note, opt.octave)}
              >
                {formatAddOptionLabel(opt.note, opt.octave)}
              </button>
            );
          })}
        </div>
      </div>

      {!isCustom && (
        <p className="chord-voicing-editor__hint">
          Tríada por defecto:{' '}
          {defaultVoicing(chordId).map((f) => freqToSolfegeLabel(f)).join(' · ')}
        </p>
      )}
    </div>
  );
}
